import { Head, Link, router } from "@inertiajs/react"
import { Settings, Shield } from "lucide-react"
import { useCallback, useState } from "react"

import { Heading } from "@/components/heading"
import { HuePicker } from "@/components/hue-picker"
import { RaceShield } from "@/components/race-shield"
import { RaceTag } from "@/components/race-tag"
import { TeamPhotoTriptych } from "@/components/team-photo-triptych"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { formatGold } from "@/lib/format"
import { cn } from "@/lib/utils"
import { leaguePath, leagueTeamPath, manageLeagueTeamPath } from "@/routes"
import type { BreadcrumbItem, TeamPlayer, TeamShowProps } from "@/types"

import { FirePlayerDialog } from "./fire-player-dialog"
import { RosterTable } from "./roster-table"

function TeamColorEditor({
  race,
  colorHue,
  leagueId,
  teamId,
}: {
  race: string
  colorHue: number | null
  leagueId: string
  teamId: string
}) {
  const [localHue, setLocalHue] = useState<number | null>(colorHue)

  const handleChangeEnd = useCallback(
    (hue: number | null) => {
      router.patch(
        leagueTeamPath(leagueId, teamId),
        { team: { color_hue: hue } },
        { preserveScroll: true },
      )
    },
    [leagueId, teamId],
  )

  return (
    <div className="mb-6 max-w-sm">
      <Label
        htmlFor="team-color"
        className="mb-2 block text-xs font-bold tracking-widest uppercase"
        style={{ color: "var(--accent-dim)" }}
      >
        Team Color
      </Label>
      <HuePicker
        id="team-color"
        race={race}
        value={localHue}
        onChange={setLocalHue}
        onChangeEnd={handleChangeEnd}
      />
    </div>
  )
}

export default function TeamShow({
  league,
  team,
  team_photo_urls,
  is_owner,
}: TeamShowProps) {
  const [fireDialogPlayer, setFireDialogPlayer] = useState<TeamPlayer | null>(
    null,
  )
  const alivePlayerCount = team.players.filter(
    (p) => p.status !== "dead",
  ).length
  const canFire = alivePlayerCount > 11

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
    { title: team.name },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${team.name} · ${league.name}`} />

      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        {/* Team header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-start gap-4">
            <RaceShield
              race={team.race}
              hue={team.color_hue ?? undefined}
              size="lg"
              className="flex-shrink-0"
            />
            <div>
              <Heading level={1} wrapperClassName="mb-1">
                {team.name}
              </Heading>
              <div className="flex items-center gap-3">
                <RaceTag race={team.race} hue={team.color_hue ?? undefined} />
                <span className="text-muted-foreground text-sm">
                  Coach {team.coach_name}
                </span>
              </div>
            </div>
          </div>
          {is_owner && (
            <Button
              asChild
              variant="default"
              className="inline-flex h-10 cursor-pointer items-center justify-center px-5 text-sm font-bold tracking-wider uppercase"
            >
              <Link href={manageLeagueTeamPath(league.id, team.id)}>
                <Settings className="mr-2 size-4" />
                Manage Team
              </Link>
            </Button>
          )}
        </div>

        {/* Team photo */}
        {team_photo_urls && (
          <TeamPhotoTriptych urls={team_photo_urls} teamName={team.name} />
        )}

        {/* Inline team color editor (owner only) */}
        {is_owner && (
          <TeamColorEditor
            race={team.race}
            colorHue={team.color_hue}
            leagueId={league.id}
            teamId={team.id}
          />
        )}

        {/* Summary stats */}
        <div className="mb-10">
          <Card className="flex flex-row gap-0 py-0">
            <CardContent className="flex flex-1 p-0">
              {[
                {
                  label: "Team Value",
                  value: formatGold(team.team_value),
                  sub: "gold pieces",
                  gold: true,
                },
                {
                  label: "Treasury",
                  value: formatGold(team.treasury_balance),
                  sub: "gold pieces",
                  gold: false,
                },
                {
                  label: "Players",
                  value: String(team.players.length),
                  sub: "on roster",
                  gold: false,
                },
                {
                  label: "Record",
                  value: "0-0-0",
                  sub: "W / D / L",
                  gold: false,
                },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  className={cn(
                    "flex-1 px-2 py-3 text-center",
                    i > 0 && "border-border border-l",
                  )}
                >
                  <p className="text-xs font-bold tracking-widest text-[var(--accent-dim)] uppercase">
                    {stat.label}
                  </p>
                  <p
                    className="font-display mt-1 text-2xl leading-none font-bold"
                    style={
                      stat.gold
                        ? {
                            color: "var(--accent)",
                            textShadow:
                              "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
                          }
                        : undefined
                    }
                  >
                    {stat.value}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-[10px] tracking-wide uppercase">
                    {stat.sub}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Staff & extras */}
        <div className="mb-10">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Shield className="size-4" />
                Staff &amp; Extras
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    Rerolls
                  </dt>
                  <dd className="mt-0.5 font-semibold">
                    {team.rerolls}
                    <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                      ({formatGold(team.reroll_cost)} each)
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    Apothecary
                  </dt>
                  <dd className="mt-0.5 font-semibold">
                    {team.apothecary ? "Yes" : "No"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    Asst. Coaches
                  </dt>
                  <dd className="mt-0.5 font-semibold">
                    {team.assistant_coaches}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    Cheerleaders
                  </dt>
                  <dd className="mt-0.5 font-semibold">{team.cheerleaders}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>

        {/* Roster table */}
        <RosterTable
          players={team.players}
          teamValue={team.team_value}
          leagueId={league.id}
          teamId={team.id}
          isOwner={is_owner}
          canFire={canFire}
          onFireClick={setFireDialogPlayer}
        />

        {/* Back link */}
        <div className="text-center">
          <Link
            href={leaguePath(league.id)}
            className="text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            ← Back to {league.name}
          </Link>
        </div>
      </div>

      {/* Dialogs */}
      {is_owner && fireDialogPlayer && (
        <FirePlayerDialog
          player={fireDialogPlayer}
          leagueId={league.id}
          teamId={team.id}
          open={!!fireDialogPlayer}
          onOpenChange={(open) => {
            if (!open) setFireDialogPlayer(null)
          }}
        />
      )}
    </AppLayout>
  )
}
